import { flow } from 'fp-ts/lib/function';
import { chainW, tryCatch } from 'fp-ts/lib/TaskEither';

import { useTRPC } from '../../../hooks/useTRPC';
import { typedError } from '../../../lib/typedError';
import * as Form from '../../Form';

import { FormData } from './FormData';

interface Props {
  className?: string;
  onComplete?(formData: FormData): void;
  initialFormData?: FormData;
}

export function CreateIndividual(props: Props) {
  const trpc = useTRPC();

  const uploadAvatar = async (
    file: File,
  ): Promise<{ originalUrl: string; thumbnailUrl: string }> => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';

    console.log('[uploadAvatar] Starting upload for file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      fileExtension,
    });

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const result = await trpc.uploadImageWithThumbnail.mutate({
      imageBase64: base64,
      folder: 'avatars',
      fileExtension,
    });

    if (result._tag === 'Left') {
      throw new Error(result.left.message);
    }

    const { originalUrl, thumbnailUrl } = result.right;

    console.log('[uploadAvatar] Upload successful:', {
      originalUrl,
      thumbnailUrl,
    });

    return { originalUrl, thumbnailUrl };
  };

  return (
    <Form.Root
      key={props.initialFormData ? 'with-data' : 'empty'}
      className={props.className}
      formDataShape={FormData}
      validityKey={props.initialFormData ? 'check-validity' : undefined}
      onComplete={props.onComplete}
      onSubmitData={flow(
        chainW((formData: FormData) =>
          tryCatch(
            async () => {
              let avatarUrl: string | undefined;
              let avatarThumbnailUrl: string | undefined;

              if (formData.avatar) {
                const uploadResult = await uploadAvatar(formData.avatar);
                avatarUrl = uploadResult.originalUrl;
                avatarThumbnailUrl = uploadResult.thumbnailUrl;
              }

              const result = await trpc.createPublisherIndividual.mutate({
                name: formData.name,
                website: formData.website,
                avatarUrl,
                avatarThumbnailUrl,
              });

              if (result._tag === 'Left') {
                throw new Error(result.left.message);
              }

              return result.right;
            },
            (error) => {
              // Extract error message from various error types
              let errorMessage = 'An error occurred';

              if (error instanceof Error) {
                errorMessage = error.message;
              } else if (
                error &&
                typeof error === 'object' &&
                'message' in error
              ) {
                errorMessage = String(error.message);
              } else if (typeof error === 'string') {
                errorMessage = error;
              }

              return typedError('InvalidFormData', errorMessage);
            },
          ),
        ),
      )}
    >
      <div className="space-y-6">
        <Form.Input
          required
          label="Name"
          name="name"
          defaultValue={props.initialFormData?.name}
        />
        <Form.UrlInput
          required
          label="Website"
          name="website"
          placeholder="https://"
          defaultValue={props.initialFormData?.website}
        />
        <Form.FileInput
          label="Publisher Avatar (Optional)"
          maxSize={3000000}
          name="avatar"
        />
      </div>
      <div className="my-16 w-full">
        <Form.Submit className="w-full">Next</Form.Submit>
        <Form.Error className="mt-1" />
      </div>
    </Form.Root>
  );
}
